import React, { useState, useEffect } from 'react'
import {
  PlusIcon,
  HeartIcon,
  SwatchIcon,
  LockClosedIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { FolderOpenIcon } from '@heroicons/react/24/solid'
import TagCloud from './TagCloud'
import FolderTreeRenderer from './FolderTreeRenderer'
import toast from 'react-hot-toast'
import { folderApi } from '../../../shared/services/folderApi'
import ConfirmDialog from '../../../shared/components/ConfirmDialog'
import CreateFolderModal from './folder/CreateFolderModal'
import EditFolderModal from './folder/EditFolderModal'
import DroppableFolderItem from './folder/DroppableFolderItem'
import type { FolderNode as Folder } from './folder/types'
import { useTranslation } from 'react-i18next'
import SavedFilterList from './bookmark/SavedFilterList'
import type { BookmarkPresetFilter } from '../../../types/bookmark'

interface FolderSidebarProps {
  onFolderSelect: (folderId: string | null) => void
  selectedFolder: string | null
  showFavorites: boolean
  onToggleFavorites: () => void
  selectedTags?: string[]
  onTagClick?: (tagId: string) => void
  onClearTags?: () => void
  onDeleteTag?: (tagId: string) => void
  refreshTrigger?: number // 외부에서 갱신을 트리거하기 위한 props
  onFolderChange?: () => void // 폴더 변경사항 발생 시 부모에게 알리는 콜백
  securityFilter: string | null // 보안 필터 상태
  onSecurityFilterChange: (filter: string | null) => void // 보안 필터 변경 함수
  presetFilter: BookmarkPresetFilter | null // 저장된 필터 상태
  onPresetFilterChange: (preset: BookmarkPresetFilter | null) => void
}

const FolderSidebar: React.FC<FolderSidebarProps> = ({
  onFolderSelect,
  selectedFolder,
  showFavorites,
  onToggleFavorites,
  selectedTags = [],
  onTagClick,
  onClearTags,
  onDeleteTag,
  refreshTrigger,
  onFolderChange,
  securityFilter,
  onSecurityFilterChange,
  presetFilter,
  onPresetFilterChange,
}) => {
  const { t } = useTranslation()
  const [folders, setFolders] = useState<Folder[]>([])
  const [folderTree, setFolderTree] = useState<Folder[]>([])
  const [loading, setLoading] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createModalParent, setCreateModalParent] = useState<string | null>(
    null
  )
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<Folder | null>(
    null
  )
  const [totalLinks, setTotalLinks] = useState(0)
  const [favoriteLinks, setFavoriteLinks] = useState(0)
  const [isChangingColors, setIsChangingColors] = useState(false)
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())

  // 색상 팔레트 정의
  const colorPalette = [
    '#3B82F6',
    '#10B981',
    '#F59E0B',
    '#EF4444',
    '#8B5CF6',
    '#06B6D4',
    '#F97316',
    '#84CC16',
    '#EC4899',
    '#6366F1',
    '#14B8A6',
    '#F43F5E',
    '#8E4EC6',
    '#059669',
    '#DC2626',
    '#7C3AED',
    '#0891B2',
    '#EA580C',
    '#65A30D',
    '#BE185D',
    '#4F46E5',
    '#0D9488',
    '#B91C1C',
    '#7C2D12',
    '#DBEAFE',
    '#D1FAE5',
    '#FEF3C7',
    '#FEE2E2',
    '#EDE9FE',
    '#CFFAFE',
    '#FFEDD5',
    '#ECFDF5',
    '#FCE7F3',
    '#E0E7FF',
    '#CCFBF1',
    '#FFE4E6',
    '#F3E8FF',
    '#A7F3D0',
    '#FECACA',
    '#C7D2FE',
    '#99F6E4',
    '#FED7AA',
    '#D9F99D',
    '#F9A8D4',
    '#A5B4FC',
    '#5EEAD4',
    '#FCA5A5',
    '#DDD6FE',
  ]

  // 폴더들의 색상을 자동으로 변경하는 함수
  const handleAutoChangeColors = async () => {
    if (folders.length === 0 || isChangingColors) {
      return
    }

    setIsChangingColors(true)

    try {
      // 현재 사용 중인 색상들을 제외한 색상 팔레트 생성
      const availableColors = [...colorPalette]

      // 각 폴더에 새로운 색상 할당
      const colorPromises = folders.map(async folder => {
        // 랜덤 색상 선택 (현재 색상과 다른 색상)
        const filteredColors = availableColors.filter(
          color => color !== folder.color
        )
        const randomColor =
          filteredColors[Math.floor(Math.random() * filteredColors.length)]

        // 선택된 색상을 사용 가능한 색상에서 제거 (중복 방지)
        const colorIndex = availableColors.indexOf(randomColor)
        if (colorIndex > -1) {
          availableColors.splice(colorIndex, 1)
        }

        // 서버에 색상 업데이트 요청
        return folderApi.update(folder._id, {
          name: folder.name,
          color: randomColor,
          description: folder.description,
        })
      })

      // 모든 폴더 색상 업데이트 완료 대기
      await Promise.all(colorPromises)

      // 폴더 목록 새로고침
      await fetchFolders()

      // 부모 컴포넌트에 변경사항 알림
      if (onFolderChange) {
        onFolderChange()
      }
    } catch (error) {
      console.error('폴더 색상 변경 실패:', error)
      toast.error(t('folder.colorChangeError'))
    } finally {
      setIsChangingColors(false)
    }
  }

  // 폴더 목록을 트리 구조로 변환하는 함수
  const buildFolderTree = (folders: Folder[]): Folder[] => {
    const folderMap = new Map<string, Folder>()
    const rootFolders: Folder[] = []

    // 먼저 모든 폴더를 맵에 저장하고 children 배열 초기화
    folders.forEach(folder => {
      folderMap.set(folder._id, { ...folder, children: [] })
    })

    // 트리 구조 구성
    folders.forEach(folder => {
      const folderWithChildren = folderMap.get(folder._id)!

      if (folder.parentFolder) {
        // 부모 폴더가 있는 경우 부모의 children에 추가
        const parent = folderMap.get(folder.parentFolder)
        if (parent && parent.children) {
          parent.children.push(folderWithChildren)
        } else {
          // 부모를 찾을 수 없으면 루트에 추가 (데이터 정합성 보장)
          rootFolders.push(folderWithChildren)
        }
      } else {
        // 부모가 없으면 루트 폴더
        rootFolders.push(folderWithChildren)
      }
    })

    // 각 레벨에서 폴더를 이름순으로 정렬
    const sortFolders = (folders: Folder[]): void => {
      folders.sort((a, b) => a.name.localeCompare(b.name))
      folders.forEach(folder => {
        if (folder.children && folder.children.length > 0) {
          sortFolders(folder.children)
        }
      })
    }

    sortFolders(rootFolders)
    return rootFolders
  }

  // 폴더 확장/축소 토글
  const toggleFolderExpanded = (folderId: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev)
      if (newSet.has(folderId)) {
        newSet.delete(folderId)
      } else {
        newSet.add(folderId)
      }
      return newSet
    })
  }

  useEffect(() => {
    fetchFolders()
  }, [refreshTrigger]) // refreshTrigger가 변경될 때마다 폴더 목록 새로고침

  const fetchFolders = async () => {
    setLoading(true)
    try {
      const result = await folderApi.listWithCounts()
      const fetchedFolders = result.folders as unknown as Folder[]
      setFolders(fetchedFolders)
      setFolderTree(buildFolderTree(fetchedFolders))
      setTotalLinks(result.totalLinks)
      setFavoriteLinks(result.favoriteLinks)
    } catch (error) {
      console.error('폴더 조회 실패:', error)
      toast.error(t('folder.loadError'))
    } finally {
      setLoading(false)
    }
  }

  const handleEditFolder = (folder: Folder) => {
    setEditingFolder(folder)
  }

  const handleDeleteFolder = async (folder: Folder) => {
    if (folder.isDefault) {
      toast.error(t('folder.deleteDefaultError'))
      return
    }

    try {
      await folderApi.remove(folder._id)
      {
        // 삭제된 폴더를 목록에서 제거
        setFolders(prev => prev.filter(f => f._id !== folder._id))

        // 현재 선택된 폴더가 삭제된 폴더면 "모든 링크"로 이동
        if (selectedFolder === folder._id) {
          onFolderSelect(null)
        }

        // 모달 닫기
        setShowDeleteConfirm(null)

        // 폴더 목록 새로고침
        await fetchFolders()

        // 부모 컴포넌트에 변경사항 알림 (URL 목록 갱신용)
        if (onFolderChange) {
          onFolderChange()
        }

        toast.success(t('folder.deleted', { name: folder.name }))
      }
    } catch (error) {
      console.error('폴더 삭제 실패:', error)
      toast.error(
        error instanceof Error ? error.message : t('folder.deleteError')
      )
    }
  }

  return (
    <div className="h-full w-72 overflow-y-auto border-r border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 3xl:w-80 5xl:w-96 7xl:w-[400px]">
      {/* 헤더 */}
      <div className="border-b border-gray-200 p-4 dark:border-gray-700 3xl:p-5 7xl:p-6">
        <div className="mb-2 flex items-center justify-between 3xl:mb-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white 3xl:text-xl 7xl:text-2xl">
            {t('folder.title')}
          </h2>
          <div className="flex items-center space-x-1 3xl:space-x-1.5">
            {/* 폴더 색상 자동 변경 버튼 - 사용자 폴더가 있을 때만 표시 */}
            {folders.length > 0 && (
              <button
                onClick={handleAutoChangeColors}
                disabled={isChangingColors}
                className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-gray-700 dark:hover:text-gray-300 3xl:p-1.5 7xl:p-2"
                title={t('folder.autoColor')}
              >
                {isChangingColors ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600 3xl:h-6 3xl:w-6 7xl:h-7 7xl:w-7"></div>
                ) : (
                  <SwatchIcon className="h-5 w-5 3xl:h-6 3xl:w-6 7xl:h-7 7xl:w-7" />
                )}
              </button>
            )}

            {/* 새 폴더 만들기 버튼 */}
            <button
              onClick={() => {
                setShowCreateModal(true)
                setCreateModalParent(null)
              }}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300 3xl:p-1.5 7xl:p-2"
              title={t('folder.create')}
            >
              <PlusIcon className="h-5 w-5 3xl:h-6 3xl:w-6 7xl:h-7 7xl:w-7" />
            </button>
          </div>
        </div>

        {/* 색상 변경 안내 텍스트 - 사용자 폴더가 있을 때만 표시 */}
        {folders.length > 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400 3xl:text-sm 7xl:text-base">
            색상 아이콘을 클릭하여 모든 폴더의 색상을 자동으로 변경할 수
            있습니다.
          </p>
        )}
      </div>
      {/* 폴더 목록 */}
      <div className="flex-1 overflow-y-auto">
        {/* 기본 섹션들과 사용자 폴더들을 모두 동일한 패딩으로 통일 */}
        <div className="space-y-1 p-2 3xl:space-y-1.5 3xl:p-3 7xl:p-4">
          {/* 즐겨찾기 */}
          <button
            onClick={onToggleFavorites}
            className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition-colors 3xl:px-4 3xl:py-2.5 3xl:text-base 7xl:px-5 7xl:py-3 7xl:text-lg ${
              showFavorites
                ? 'border border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300'
                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
          >
            <div className="mr-2 flex min-w-0 flex-1 items-center">
              <HeartIcon
                className={`mr-3 h-4 w-4 flex-shrink-0 3xl:mr-4 3xl:h-5 3xl:w-5 7xl:h-6 7xl:w-6 ${showFavorites ? 'text-red-500' : 'text-gray-400'}`}
              />
              <span className="truncate font-medium">
                {t('folder.favorites')}
              </span>
            </div>
            <span className="flex-shrink-0 rounded-full bg-gray-200 px-2 py-1 text-xs text-gray-600 dark:bg-gray-600 dark:text-gray-300 3xl:px-2.5 3xl:py-1 3xl:text-sm 7xl:px-3 7xl:py-1.5 7xl:text-base">
              {favoriteLinks}
            </span>
          </button>

          {/* 비밀번호 보호된 링크 */}
          <button
            onClick={() => {
              if (securityFilter === 'password') {
                onSecurityFilterChange(null)
              } else {
                onSecurityFilterChange('password')
              }
            }}
            className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition-colors 3xl:px-4 3xl:py-2.5 3xl:text-base 7xl:px-5 7xl:py-3 7xl:text-lg ${
              securityFilter === 'password'
                ? 'border border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700 dark:text-gray-300 dark:hover:bg-blue-900/20 dark:hover:text-blue-300'
            }`}
          >
            <div className="mr-2 flex min-w-0 flex-1 items-center">
              <LockClosedIcon className="mr-3 h-4 w-4 flex-shrink-0 text-blue-500 3xl:mr-4 3xl:h-5 3xl:w-5 7xl:h-6 7xl:w-6" />
              <span className="truncate font-medium">
                {t('folder.passwordProtected')}
              </span>
            </div>
            <span className="flex-shrink-0 rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 3xl:px-2.5 3xl:py-1 3xl:text-sm 7xl:px-3 7xl:py-1.5 7xl:text-base">
              Growth+
            </span>
          </button>

          {/* 만료 예정 링크 */}
          <button
            onClick={() => {
              if (securityFilter === 'expiring') {
                onSecurityFilterChange(null)
              } else {
                onSecurityFilterChange('expiring')
              }
            }}
            className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition-colors 3xl:px-4 3xl:py-2.5 3xl:text-base 7xl:px-5 7xl:py-3 7xl:text-lg ${
              securityFilter === 'expiring'
                ? 'border border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                : 'text-gray-700 hover:bg-yellow-50 hover:text-yellow-700 dark:text-gray-300 dark:hover:bg-yellow-900/20 dark:hover:text-yellow-300'
            }`}
          >
            <div className="mr-2 flex min-w-0 flex-1 items-center">
              <ClockIcon className="mr-3 h-4 w-4 flex-shrink-0 text-yellow-500 3xl:mr-4 3xl:h-5 3xl:w-5 7xl:h-6 7xl:w-6" />
              <span className="truncate font-medium">
                {t('folder.expiringSoon')}
              </span>
            </div>
            <span className="flex-shrink-0 rounded-full bg-yellow-100 px-2 py-1 text-xs text-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-400 3xl:px-2.5 3xl:py-1 3xl:text-sm 7xl:px-3 7xl:py-1.5 7xl:text-base">
              Growth+
            </span>
          </button>

          {/* 만료된 링크 */}
          <button
            onClick={() => {
              if (securityFilter === 'expired') {
                onSecurityFilterChange(null)
              } else {
                onSecurityFilterChange('expired')
              }
            }}
            className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition-colors 3xl:px-4 3xl:py-2.5 3xl:text-base 7xl:px-5 7xl:py-3 7xl:text-lg ${
              securityFilter === 'expired'
                ? 'border border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300'
                : 'text-gray-700 hover:bg-red-50 hover:text-red-700 dark:text-gray-300 dark:hover:bg-red-900/20 dark:hover:text-red-300'
            }`}
          >
            <div className="mr-2 flex min-w-0 flex-1 items-center">
              <ExclamationTriangleIcon className="mr-3 h-4 w-4 flex-shrink-0 text-red-500 3xl:mr-4 3xl:h-5 3xl:w-5 7xl:h-6 7xl:w-6" />
              <span className="truncate font-medium">
                {t('folder.expired')}
              </span>
            </div>
            <span className="flex-shrink-0 rounded-full bg-red-100 px-2 py-1 text-xs text-red-600 dark:bg-red-900/20 dark:text-red-400 3xl:px-2.5 3xl:py-1 3xl:text-sm 7xl:px-3 7xl:py-1.5 7xl:text-base">
              Growth+
            </span>
          </button>

          <SavedFilterList
            value={presetFilter}
            onChange={onPresetFilterChange}
          />

          {/* 모든 링크 */}
          <DroppableFolderItem
            id="folder-all"
            selected={!selectedFolder && !showFavorites}
            onClick={() => onFolderSelect(null)}
          >
            <div className="flex w-full items-center justify-between">
              <div className="mr-2 flex min-w-0 flex-1 items-center">
                <FolderOpenIcon className="mr-3 h-4 w-4 flex-shrink-0 text-gray-400 3xl:mr-4 3xl:h-5 3xl:w-5 7xl:h-6 7xl:w-6" />
                <span className="truncate font-medium">모든 링크</span>
              </div>
              <span className="flex-shrink-0 rounded-full bg-gray-200 px-2 py-1 text-xs text-gray-600 dark:bg-gray-600 dark:text-gray-300 3xl:px-2.5 3xl:py-1 3xl:text-sm 7xl:px-3 7xl:py-1.5 7xl:text-base">
                {totalLinks}
              </span>
            </div>
          </DroppableFolderItem>

          {/* 로딩 상태 */}
          {loading ? (
            <div className="py-8 text-center 3xl:py-10 7xl:py-12">
              <div className="mx-auto h-6 w-6 animate-spin rounded-full border-b-2 border-blue-600 3xl:h-8 3xl:w-8 7xl:h-10 7xl:w-10"></div>
            </div>
          ) : (
            /* 사용자 폴더 목록 - 트리 구조로 렌더링 */
            <FolderTreeRenderer
              folders={folderTree}
              selectedFolder={selectedFolder}
              expandedFolders={expandedFolders}
              onFolderSelect={onFolderSelect}
              onToggleExpanded={toggleFolderExpanded}
              onEditFolder={handleEditFolder}
              onDeleteFolder={folder => setShowDeleteConfirm(folder)}
              onCreateSubFolder={parentFolderId => {
                setCreateModalParent(parentFolderId)
                setShowCreateModal(true)
              }}
            />
          )}
        </div>
      </div>

      {/* 태그 클라우드 */}
      {onTagClick && onClearTags && (
        <TagCloud
          selectedTags={selectedTags}
          onTagClick={onTagClick}
          onClearTags={onClearTags}
          onDeleteTag={onDeleteTag}
        />
      )}

      {/* 새 폴더 생성 모달 */}
      {showCreateModal && (
        <CreateFolderModal
          onClose={() => {
            setShowCreateModal(false)
            setCreateModalParent(null)
          }}
          onFolderCreated={parentId => {
            fetchFolders()
            setShowCreateModal(false)

            // 서브 폴더가 생성된 경우 부모 폴더를 자동으로 확장
            if (parentId) {
              setExpandedFolders(prev => new Set([...prev, parentId]))
            }

            setCreateModalParent(null)
            // 부모 컴포넌트에 변경사항 알림
            if (onFolderChange) {
              onFolderChange()
            }
          }}
          folders={folders}
          defaultParent={createModalParent}
        />
      )}

      {/* 폴더 편집 모달 */}
      {editingFolder && (
        <EditFolderModal
          folder={editingFolder}
          onClose={() => setEditingFolder(null)}
          onFolderUpdated={() => {
            fetchFolders()
            setEditingFolder(null)
            // 부모 컴포넌트에 변경사항 알림
            if (onFolderChange) {
              onFolderChange()
            }
          }}
        />
      )}

      {/* 폴더 삭제 확인 */}
      <ConfirmDialog
        isOpen={!!showDeleteConfirm}
        title={t('folder.deleteTitle', { name: showDeleteConfirm?.name ?? '' })}
        description={t('folder.deleteDescription')}
        confirmLabel={t('common.delete')}
        onCancel={() => setShowDeleteConfirm(null)}
        onConfirm={() => {
          const target = showDeleteConfirm
          setShowDeleteConfirm(null)
          if (target) {
            handleDeleteFolder(target)
          }
        }}
      />
    </div>
  )
}

export default FolderSidebar
